import { ReactElement } from "react";
import {
  IonButton,
  IonCheckbox,
  IonIcon,
  IonItem,
  IonLabel,
} from "@ionic/react";
import {
  CheckboxQuestionType,
  CheckboxAnswerType,
} from "../../../../types/Question";
import { volumeHighOutline } from "ionicons/icons";
import useVocalisation from "../../../../hooks/useVocalisation";
import { useConfig } from "../../../../contexts/ConfigContext";
import SpeechToTextRow from "./SpeechToTextRow";

interface AnswerCheckboxProps {
  question: CheckboxQuestionType;
  value?: number[];
  onChange: (value: number[]) => void;
}

const DISABLE_SPEECH_RECOGNITION = true;

const AnswerCheckbox = ({
  question,
  value = [],
  onChange,
}: AnswerCheckboxProps): ReactElement => {
  const {
    detail: { answers },
  } = question;

  const lastAnswerIndex = answers.length - 1;

  const { selectedRole } = useConfig();
  const isVocalisationEnabled =
    selectedRole?.attributes?.is_vocalisation_enabled;
  const isSpeechRecEnabled =
    selectedRole?.attributes?.is_speech_recognition_enabled;

  const handleCheckboxChange = (answerId: number, isChecked: boolean): void => {
    if (isChecked) {
      onChange([...value, answerId]);
    } else {
      onChange([
        ...value.filter(
          (checkedItem: number): boolean => checkedItem !== answerId
        ),
      ]);
    }
  };

  const { startListnening, readText, isListening } = useVocalisation(
    answers.map((a) => a.label),
    (value) =>
      handleCheckboxChange(
        answers.find((answer) => answer.label === value).id as any,
        true
      )
  );

  const renderAnswer = (
    { label, id }: CheckboxAnswerType,
    index: number
  ): ReactElement => (
    <IonItem key={id} lines={lastAnswerIndex === index ? "none" : "full"} mode="md">
      <IonCheckbox
        slot="start"
        value={label}
        checked={value.includes(id)}
        onIonChange={(e: CustomEvent): void =>
          handleCheckboxChange(id, e.detail.checked)
        }
      />
      <IonLabel>
        <p className="answer-with-scroll">{label}</p>
      </IonLabel>
      {isVocalisationEnabled && (
        <IonButton
          slot="end"
          fill="clear"
          onClick={(): void => {
            readText(label);
          }}
        >
          <IonIcon slot="icon-only" icon={volumeHighOutline} />
        </IonButton>
      )}
    </IonItem>
  );

  return (
    <>
    {/* Full height on purpose: a capped box with its own scroll hid the last
        choices (« Autre ») behind a second scrollbar inside the page. */}
    <div className="answer-checkbox-list">
      {answers.map(renderAnswer)}
      </div>
      {!DISABLE_SPEECH_RECOGNITION && isSpeechRecEnabled && (
        <SpeechToTextRow isListening={isListening} onStart={startListnening} lines="full" mode="md" />
      )}
    </>
  );
};

export default AnswerCheckbox;
